// ============================================================
// ADMIN ROUTES — All route definitions for the admin panel
// ============================================================

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import ProtectedRoute from './ProtectedRoute';
import RoleGuard from './RoleGuard';
import AdminLayout from '../components/layout/AdminLayout';

// Pages
import LoginPage from '../pages/LoginPage';
import Dashboard from '../pages/Dashboard';
import UserManagement from '../pages/UserManagement';
import RestaurantManagement from '../pages/RestaurantManagement';
import RestaurantDetails from '../pages/RestaurantDetails';
import CreateRestaurant from '../pages/CreateRestaurant';
import CustomerManagement from '../pages/CustomerManagement';
import CustomerDetails from '../pages/CustomerDetails';
import Orders from '../pages/Orders';
import Finance from '../pages/Finance';
import SupportTickets from '../pages/SupportTickets';
import RolesPermissions from '../pages/RolesPermissions';
import Settings from '../pages/Settings';
import AdminProfile from '../pages/AdminProfile';

// Legacy pages kept for backward-compat deep links
import PickupQueue from '../pages/PickupQueue';
import PreparationAnalytics from '../pages/PreparationAnalytics';
import PickupAnalytics from '../pages/PickupAnalytics';
import LegalVerification from '../pages/LegalVerification';
import FoodCompliance from '../pages/FoodCompliance';
import Statistics from '../pages/Statistics';
import SalesReports from '../pages/SalesReports';
import RevenuePayout from '../pages/RevenuePayout';
import CommissionTracking from '../pages/CommissionTracking';
import CommissionSettings from '../pages/CommissionSettings';
import BankDetails from '../pages/BankDetails';
import OrderRejections from '../pages/OrderRejections';
import OrderCancellations from '../pages/OrderCancellations';
import FinancialSettlements from '../pages/FinancialSettlements';
import AppTesting from '../pages/AppTesting';

const AdminRoutes = () => {
  return (
    <Routes>
      {/* Public route */}
      <Route path="login" element={<LoginPage />} />

      {/* Protected routes wrapped in layout */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <Routes>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />

                {/* ── Core ─────────────────────────────────────── */}
                <Route path="dashboard" element={<Dashboard />} />

                {/* ── Restaurants ──────────────────────────────── */}
                <Route path="restaurants"           element={<RoleGuard navId="restaurants"><RestaurantManagement /></RoleGuard>} />
                <Route path="restaurants/create"    element={<RoleGuard navId="restaurants"><CreateRestaurant /></RoleGuard>} />
                <Route path="restaurants/:id"       element={<RoleGuard navId="restaurants"><RestaurantDetails /></RoleGuard>} />

                {/* ── Customers ────────────────────────────────── */}
                <Route path="customers"         element={<RoleGuard navId="customers"><CustomerManagement /></RoleGuard>} />
                <Route path="customers/:id"     element={<RoleGuard navId="customers"><CustomerDetails /></RoleGuard>} />

                {/* ── Orders ───────────────────────────────────── */}
                <Route path="orders"            element={<RoleGuard navId="orders"><Orders /></RoleGuard>} />

                {/* ── Finance ──────────────────────────────────── */}
                <Route path="finance"           element={<RoleGuard navId="finance"><Finance /></RoleGuard>} />

                {/* ── Admin ────────────────────────────────────── */}
                <Route path="users"             element={<RoleGuard navId="users"><UserManagement /></RoleGuard>} />
                <Route path="support"           element={<RoleGuard navId="support"><SupportTickets /></RoleGuard>} />
                <Route path="roles"             element={<RoleGuard navId="roles"><RolesPermissions /></RoleGuard>} />
                <Route path="settings"          element={<RoleGuard navId="settings"><Settings /></RoleGuard>} />
                <Route path="profile"           element={<AdminProfile />} />

                {/* ── Legacy backward-compat routes ─────────────── */}
                <Route path="pickup-queue"        element={<PickupQueue />} />
                <Route path="rejections"          element={<OrderRejections />} />
                <Route path="cancellations"       element={<OrderCancellations />} />
                <Route path="prep-analytics"      element={<PreparationAnalytics />} />
                <Route path="legal"               element={<LegalVerification />} />
                <Route path="compliance"          element={<FoodCompliance />} />
                <Route path="pickup-analytics"    element={<PickupAnalytics />} />
                <Route path="revenue"             element={<RevenuePayout />} />
                <Route path="commission"          element={<CommissionTracking />} />
                <Route path="commission-settings" element={<CommissionSettings />} />
                <Route path="settlements"         element={<FinancialSettlements />} />
                <Route path="bank-details"        element={<BankDetails />} />
                <Route path="statistics"          element={<Statistics />} />
                <Route path="reports"             element={<SalesReports />} />
                <Route path="app-testing"         element={<AppTesting />} />
                <Route path="testing"             element={<AppTesting />} />

                {/* ── Fallback ──────────────────────────────────── */}
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AdminRoutes;
